<!--
  THE LIVING GROUND.

  Second cut. The first was the React Bits Aurora tide with LiquidChrome
  ripples, and the owner's verdict was exact: "way too tacky and way too
  in-your-face. It creates a wave when you touch." What he asked for instead:
  "a black background with a little bit of texture and a little bit of motion
  when you click on the screen ... way more subtle."

  So this is now two things, and both are quiet:

    1. TEXTURE. A fine film grain over the ground - the thing that separates a
       black that feels like a cinema from a black that feels like a phone
       that is off. It is a tiled noise texture generated once, drifted by a
       sub-pixel amount each frame so it never reads as a static pattern, at
       ~4% opacity. You should not be able to point at it; you should only
       miss it when it is gone.

    2. A BREATH ON TOUCH. A pointerdown anywhere brightens the ground very
       slightly around the point - a soft radial lift in the theme's accent,
       peaking at ~6% and gone in ~700 ms. No ring, no wave, no displacement.
       It is the ground acknowledging the touch, not performing it.

  It only WATCHES the pointer (window listener, capture, passive). It never
  captures it, so nothing about how a control responds changes. Reduced
  motion: grain is drawn once and stays still; no breath. Television tier
  (`data-tv`): nothing at all. Pauses when hidden. Renders at half resolution.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import { theme } from './theme.svelte';

  let canvas = $state<HTMLCanvasElement | null>(null);

  const BREATH_MS = 700;

  onMount(() => {
    const root = document.documentElement;
    if (root.dataset.tv === 'on') return;
    const c = canvas!;
    const ctx = c.getContext('2d', { alpha: true });
    if (!ctx) return;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* The grain tile: 256px of monochrome noise, built once. Drawn as a
       pattern with a tiny per-frame offset so it shimmers instead of sitting. */
    const tile = document.createElement('canvas');
    tile.width = tile.height = 256;
    const tctx = tile.getContext('2d')!;
    const img = tctx.createImageData(256, 256);
    let seed = 1337;
    const rnd = (): number => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = 128 + (rnd() - 0.5) * 140;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
    tctx.putImageData(img, 0, 0);
    let pattern: CanvasPattern | null = null;

    let accent = '#ef2034';
    let light = false;
    const readTheme = (): void => {
      const cs = getComputedStyle(root);
      accent = cs.getPropertyValue('--accent').trim() || accent;
      light = root.dataset.theme === 'light';
    };
    readTheme();

    let w = 1, h = 1, dpr = 0.5;
    const size = (): void => {
      dpr = Math.min(window.devicePixelRatio || 1, 2) * 0.5;
      w = window.innerWidth; h = window.innerHeight;
      c.width = Math.max(1, Math.round(w * dpr));
      c.height = Math.max(1, Math.round(h * dpr));
      pattern = ctx.createPattern(tile, 'repeat');
    };
    size();

    type Breath = { x: number; y: number; t0: number };
    let breaths: Breath[] = [];
    const onDown = (e: PointerEvent): void => {
      if (reduced) return;
      breaths.push({ x: e.clientX, y: e.clientY, t0: performance.now() });
      if (breaths.length > 4) breaths = breaths.slice(-4);
      start();
    };

    let raf = 0, running = false;
    const draw = (now: number): void => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      /* Grain. The offset walks a slow Lissajous so the shimmer has no
         visible direction; amplitude of a few px is enough. */
      if (pattern) {
        const t = now / 1000;
        const ox = reduced ? 0 : Math.sin(t * 0.9) * 3 + Math.sin(t * 2.3) * 1.5;
        const oy = reduced ? 0 : Math.cos(t * 1.1) * 3 + Math.cos(t * 1.9) * 1.5;
        ctx.save();
        ctx.globalAlpha = light ? 0.05 : 0.045;
        ctx.globalCompositeOperation = light ? 'soft-light' : 'overlay';
        ctx.translate(ox, oy);
        ctx.fillStyle = pattern;
        ctx.fillRect(-8, -8, w + 16, h + 16);
        ctx.restore();
      }

      /* The breath. A soft lift; never a ring. */
      breaths = breaths.filter((b) => now - b.t0 < BREATH_MS);
      for (const b of breaths) {
        const p = (now - b.t0) / BREATH_MS;
        const ease = Math.sin(p * Math.PI);         /* rise then fall */
        const rad = Math.min(w, h) * (0.18 + p * 0.10);
        const g = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, rad);
        const a = Math.round(ease * 0x10).toString(16).padStart(2, '0');
        g.addColorStop(0, accent + a);
        g.addColorStop(1, accent + '00');
        ctx.fillStyle = g;
        ctx.fillRect(b.x - rad, b.y - rad, rad * 2, rad * 2);
      }
    };
    const tick = (now: number): void => {
      draw(now);
      if (reduced && breaths.length === 0) { running = false; return; }
      raf = requestAnimationFrame(tick);
    };
    const start = (): void => { if (!running) { running = true; raf = requestAnimationFrame(tick); } };
    const stop = (): void => { running = false; cancelAnimationFrame(raf); };

    const onVis = (): void => { document.hidden ? stop() : start(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('resize', size, { passive: true });
    window.addEventListener('pointerdown', onDown, { capture: true, passive: true });
    const mo = new MutationObserver(readTheme);
    mo.observe(root, { attributes: true, attributeFilter: ['data-theme', 'style'] });

    if (reduced) draw(performance.now()); else start();
    return () => {
      stop();
      mo.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('resize', size);
      window.removeEventListener('pointerdown', onDown, { capture: true } as EventListenerOptions);
    };
  });

  $effect(() => { void theme.id; });
</script>

<canvas class="ground" bind:this={canvas} aria-hidden="true"></canvas>

<style>
  .ground {
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    /* -1 inside #app's isolated stacking context (app.css `isolation:
       isolate`): under every screen, bar and sheet, above <body>'s ground. */
    z-index: -1;
    pointer-events: none;
  }
  :global(html[data-tv='on']) .ground {
    display: none;
  }
</style>
